import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  SOFT_DELETE_TABLES,
  fetchTrashCounts,
  listDeletedRows,
  purgeRow,
  restoreRow,
  softDeleteTableMeta,
  type DeletedRow,
} from '@/lib/coreAudit';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

export default function TrashTab({ org }: Props) {
  const { user } = useAuth();
  const [table, setTable] = useState(SOFT_DELETE_TABLES[0].table);
  const [rows, setRows] = useState<DeletedRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirmPurge, setConfirmPurge] = useState<DeletedRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, c] = await Promise.all([listDeletedRows(table, org.id), fetchTrashCounts(org.id)]);
      setRows(list);
      setCounts(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id, table]);

  useEffect(() => { load(); }, [load]);

  const restore = async (r: DeletedRow) => {
    setBusyId(r.id);
    try { await restoreRow(table, r.id); load(); } finally { setBusyId(null); }
  };

  const purge = async (r: DeletedRow) => {
    setBusyId(r.id);
    try { await purgeRow(table, r.id); setConfirmPurge(null); load(); } finally { setBusyId(null); }
  };

  void user;

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        Soft Delete : les données importantes ne sont pas supprimées physiquement tout de suite. Elles sont marquées
        (<code className="text-[11px]">deleted_at / deleted_by / archived_at / archived_by</code>) et restent <span className="font-semibold text-foreground-700">restaurables</span>.
        La suppression définitive reste possible pour les données légalement ou techniquement supprimables.
      </p>

      <div className="flex flex-wrap gap-2">
        {SOFT_DELETE_TABLES.map((t) => {
          const count = counts[t.table] || 0;
          return (
            <button key={t.table} onClick={() => setTable(t.table)} className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${table === t.table ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}>
              <i className={t.icon}></i>{t.label}
              <span className={`text-[10px] px-1.5 rounded-full ${table === t.table ? 'bg-background-50/20' : 'bg-background-50'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-delete-bin-7-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Corbeille vide pour « {softDeleteTableMeta(table)?.label} ».</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3">
              <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0"><i className={softDeleteTableMeta(table)?.icon || 'ri-record-circle-line text-foreground-500'}></i></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-900 truncate">{r.label}</p>
                <p className="text-[11px] text-foreground-400 truncate">
                  Supprimé le {r.deleted_at ? new Date(r.deleted_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  {r.deleted_by ? ` par #${r.deleted_by}` : ''}
                </p>
              </div>
              <button onClick={() => restore(r)} disabled={busyId === r.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-arrow-go-back-line"></i> Restaurer</button>
              <button onClick={() => setConfirmPurge(r)} disabled={busyId === r.id} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50 flex-shrink-0" title="Supprimer définitivement"><i className="ri-delete-bin-line"></i></button>
            </div>
          ))}
        </div>
      )}

      {confirmPurge && (
        <Modal
          title="Suppression définitive"
          onClose={() => setConfirmPurge(null)}
          maxWidth="max-w-md"
          footer={
            <>
              <button onClick={() => setConfirmPurge(null)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={() => purge(confirmPurge)} disabled={busyId === confirmPurge.id} className="px-4 py-2 rounded-full bg-red-600 text-white text-sm font-semibold whitespace-nowrap hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {busyId === confirmPurge.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-delete-bin-line"></i>} Supprimer définitivement
              </button>
            </>
          }
        >
          <p className="text-sm text-foreground-700">
            Vous êtes sur le point de supprimer définitivement <span className="font-semibold">{confirmPurge.label}</span>.
            Cette action est <span className="font-semibold text-red-600">irréversible</span>.
          </p>
        </Modal>
      )}
    </div>
  );
}